import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { CircleUser, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useCustomer } from '../lib/customerContext';
import { customerAuth } from '../lib/api';

const formatErr = (detail, fallback = 'Something went wrong') => {
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
};

export const AuthDialog = ({ open, onOpenChange }) => {
  const { login, register } = useCustomer();
  const [tab, setTab] = useState('login');
  const [busy, setBusy] = useState(false);

  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [signupForm, setSignupForm] = useState({
    name: '', email: '', phone: '', password: '', address: '', pincode: '', gst_number: '', alternate_phone: '',
  });
  const [signupStage, setSignupStage] = useState('form'); // form | otp
  const [otp, setOtp] = useState('');

  const doLogin = async () => {
    if (!loginForm.email || !loginForm.password) { toast.error('Enter email/phone and password'); return; }
    setBusy(true);
    try {
      const c = await login(loginForm.email, loginForm.password);
      toast.success(`Welcome back, ${c.name || 'customer'}!`);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail, 'Login failed'));
    } finally { setBusy(false); }
  };

  // Step 1 of signup: validate form, then email a 6-digit OTP
  const sendSignupOtp = async () => {
    const f = signupForm;
    if (!f.name || !f.email || !f.phone || !f.password) { toast.error('Name, email, phone and password are required'); return; }
    if (!f.email.includes('@')) { toast.error('Enter a valid email'); return; }
    if (f.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setBusy(true);
    try {
      await customerAuth.sendOtp(f.email, 'register');
      setSignupStage('otp');
      toast.success('Verification code sent to your email');
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail, 'Could not send code'));
    } finally { setBusy(false); }
  };

  // Step 2 of signup: create the account with the OTP
  const doSignup = async () => {
    if (!otp || otp.length < 4) { toast.error('Enter the 6-digit code from your email'); return; }
    setBusy(true);
    try {
      const c = await register({ ...signupForm, otp });
      toast.success(`Account created & verified. Welcome, ${c.name}!`);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail, 'Signup failed'));
    } finally { setBusy(false); }
  };

  const resendSignupOtp = async () => {
    setBusy(true);
    try {
      await customerAuth.sendOtp(signupForm.email, 'register');
      toast.success('New code sent');
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail, 'Could not resend code'));
    } finally { setBusy(false); }
  };

  const su = (k) => (e) => setSignupForm((p) => ({ ...p, [k]: e.target.value }));

  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const doForgot = async () => {
    if (!forgotEmail) { toast.error('Enter your account email'); return; }
    setBusy(true);
    try {
      await customerAuth.forgotPassword(forgotEmail);
      setForgotSent(true);
    } catch {
      setForgotSent(true); // keep generic even on error
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="auth-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CircleUser className="w-5 h-5 text-orange-500" /> Wholesale Account
          </DialogTitle>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="login" data-testid="auth-tab-login">Sign In</TabsTrigger>
            <TabsTrigger value="signup" data-testid="auth-tab-signup">Sign Up</TabsTrigger>
          </TabsList>

          <TabsContent value="login" className="space-y-3 pt-2">
            {forgotMode ? (
              <div className="space-y-3" data-testid="forgot-password-panel">
                {forgotSent ? (
                  <div className="text-center py-3">
                    <p className="text-[13px] text-neutral-700">Agar is email par account hai, to reset link bhej diya gaya hai. Apna inbox (aur spam) check karein.</p>
                    <Button data-testid="forgot-back-button" variant="outline" className="mt-3 w-full" onClick={() => { setForgotMode(false); setForgotSent(false); }}>Back to Sign In</Button>
                  </div>
                ) : (
                  <>
                    <p className="text-[12px] text-neutral-600">Apni account email daalein — hum password reset link bhej denge.</p>
                    <div>
                      <Label className="text-[12px] text-neutral-600">Email</Label>
                      <Input data-testid="forgot-email-input" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="you@email.com" className="mt-1" />
                    </div>
                    <Button data-testid="forgot-submit-button" onClick={doForgot} disabled={busy} className="w-full bg-orange-500 hover:bg-orange-600">
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send reset link'}
                    </Button>
                    <button className="w-full text-[11px] text-neutral-500 hover:text-neutral-700" onClick={() => setForgotMode(false)}>← Back to Sign In</button>
                  </>
                )}
              </div>
            ) : (
              <>
                <div>
                  <Label className="text-[12px] text-neutral-600">Phone or Email</Label>
                  <Input data-testid="login-email-input" value={loginForm.email} onChange={(e) => setLoginForm((p) => ({ ...p, email: e.target.value }))} placeholder="98xxxxxxxx or you@email.com" className="mt-1" />
                </div>
                <div>
                  <Label className="text-[12px] text-neutral-600">Password</Label>
                  <Input data-testid="login-password-input" type="password" value={loginForm.password} onChange={(e) => setLoginForm((p) => ({ ...p, password: e.target.value }))} placeholder="••••••••" className="mt-1" />
                </div>
                <div className="text-right">
                  <button data-testid="forgot-password-link" className="text-[11px] text-orange-600 font-medium hover:underline" onClick={() => { setForgotMode(true); setForgotSent(false); setForgotEmail(loginForm.email); }}>Forgot password?</button>
                </div>
                <Button data-testid="login-submit-button" onClick={doLogin} disabled={busy} className="w-full bg-orange-500 hover:bg-orange-600">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sign In'}
                </Button>
                <p className="text-[11px] text-center text-neutral-500">New here? <button className="text-orange-600 font-medium" onClick={() => setTab('signup')}>Create an account</button></p>
              </>
            )}
          </TabsContent>

          <TabsContent value="signup" className="space-y-2.5 pt-2 max-h-[60vh] overflow-y-auto">
            {signupStage === 'form' ? (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <div className="col-span-2">
                    <Label className="text-[12px] text-neutral-600">Full name *</Label>
                    <Input data-testid="signup-name-input" value={signupForm.name} onChange={su('name')} placeholder="Your name / firm" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-[12px] text-neutral-600">Phone *</Label>
                    <Input data-testid="signup-phone-input" value={signupForm.phone} onChange={su('phone')} placeholder="98xxxxxxxx" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-[12px] text-neutral-600">Alternate phone</Label>
                    <Input data-testid="signup-altphone-input" value={signupForm.alternate_phone} onChange={su('alternate_phone')} placeholder="optional" className="mt-1" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-[12px] text-neutral-600">Email * <span className="text-orange-600">(OTP yahin aayega)</span></Label>
                    <Input data-testid="signup-email-input" value={signupForm.email} onChange={su('email')} placeholder="you@email.com" className="mt-1" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-[12px] text-neutral-600">Password *</Label>
                    <Input data-testid="signup-password-input" type="password" value={signupForm.password} onChange={su('password')} placeholder="min 6 characters" className="mt-1" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-[12px] text-neutral-600">Delivery address</Label>
                    <Input data-testid="signup-address-input" value={signupForm.address} onChange={su('address')} placeholder="Shop / warehouse address" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-[12px] text-neutral-600">Pincode</Label>
                    <Input data-testid="signup-pincode-input" value={signupForm.pincode} onChange={su('pincode')} placeholder="e.g. 110001" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-[12px] text-neutral-600">GST number</Label>
                    <Input data-testid="signup-gst-input" value={signupForm.gst_number} onChange={su('gst_number')} placeholder="optional" className="mt-1" />
                  </div>
                </div>
                <Button data-testid="signup-send-otp-button" onClick={sendSignupOtp} disabled={busy} className="w-full bg-orange-500 hover:bg-orange-600">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send verification code'}
                </Button>
                <p className="text-[11px] text-center text-neutral-500">Already have an account? <button className="text-orange-600 font-medium" onClick={() => setTab('login')}>Sign in</button></p>
              </>
            ) : (
              <div className="space-y-3 py-2" data-testid="signup-otp-panel">
                <p className="text-[13px] text-neutral-700">Humne <b>{signupForm.email}</b> par ek 6-digit code bheja hai. Email (aur spam) check karke code daalein.</p>
                <div>
                  <Label className="text-[12px] text-neutral-600">Verification code</Label>
                  <Input data-testid="signup-otp-input" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="6-digit code" inputMode="numeric" className="mt-1 tracking-[6px] text-center text-lg" />
                </div>
                <Button data-testid="signup-verify-button" onClick={doSignup} disabled={busy} className="w-full bg-orange-500 hover:bg-orange-600">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify & Create Account'}
                </Button>
                <div className="flex items-center justify-between text-[11px]">
                  <button className="text-neutral-500 hover:text-neutral-700" onClick={() => { setSignupStage('form'); setOtp(''); }}>← Edit details</button>
                  <button data-testid="signup-resend-otp-button" className="text-orange-600 font-medium hover:underline" onClick={resendSignupOtp} disabled={busy}>Resend code</button>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default AuthDialog;
